import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { Box, Typography } from '@mui/material'

import { Button } from '@/components'
import { Modal } from '@/components/Modal/Modal'
import { useAppContext } from '@/hooks'
import { useSelectedBranchNumericId } from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import { SET_DIFF_CATEGORY } from '@/pages/User/pages/Releases/setDiff'
import {
	DiffFooterActions,
	DiffHeaderFlex,
	LoadingPlaceholder,
	StyledDiffWrap,
} from '@/pages/User/pages/Settings/pages/Templates/styles'
import { useCompareStructureHistoryQuery } from '@/rtkApi'
import type { DiffChangeDto } from '@/rtkApi/mddApiSliceGeneratedV2'

const MODAL_MIN_WIDTH = 1000

const MODAL_CONTENT_STYLE = {
	width: MODAL_MIN_WIDTH,
	minWidth: MODAL_MIN_WIDTH,
} as const

const MODAL_BODY_STYLE = {
	display: 'flex',
	flexDirection: 'column',
} as const

const MODAL_HEADER_STYLE = { paddingBottom: 0 } as const

const CHANGE_STYLE: Record<
	NonNullable<DiffChangeDto['changeType']>,
	{ bg: string; color: string }
> = {
	ADDED: { color: SET_DIFF_CATEGORY.added.color, bg: SET_DIFF_CATEGORY.added.bg },
	CONFLICT: { color: '#C5221F', bg: '#FDE7E6' },
	MOVED: { color: '#1F4954', bg: '#E8F1F3' },
	REMOVED: {
		color: SET_DIFF_CATEGORY.removed.color,
		bg: SET_DIFF_CATEGORY.removed.bg,
	},
	MODIFIED: {
		color: SET_DIFF_CATEGORY.modified.color,
		bg: SET_DIFF_CATEGORY.modified.bg,
	},
	UNCHANGED: {
		color: SET_DIFF_CATEGORY.unchanged.color,
		bg: SET_DIFF_CATEGORY.unchanged.bg,
	},
}

const formatDiffValue = (value: unknown): string => {
	if (value === null || value === undefined) return '—'
	if (typeof value === 'string') return value
	if (typeof value === 'number' || typeof value === 'boolean') return String(value)
	try {
		return JSON.stringify(value, null, 2)
	} catch {
		return String(value)
	}
}

export type VersionDiffModalProps = {
	leftHistoryId: number
	leftVersion?: number
	onClose: () => void
	rightHistoryId: number
	rightVersion?: number
	structureId: number
	structureName?: string
}

export const VersionDiffModal = ({
	structureId,
	structureName,
	leftHistoryId,
	rightHistoryId,
	leftVersion,
	rightVersion,
	onClose,
}: VersionDiffModalProps) => {
	const { t } = useAppContext()
	const branchId = useSelectedBranchNumericId()
	const skip =
		branchId == null ||
		!structureId ||
		leftHistoryId == null ||
		rightHistoryId == null

	const { data, isError, isFetching, isLoading } =
		useCompareStructureHistoryQuery(
			{
				branchId: branchId ?? 0,
				structureId,
				leftHistoryId,
				rightHistoryId,
			},
			{ skip },
		)

	const changes = data?.changes ?? []
	const showLoading = !skip && (isLoading || isFetching)
	const leftTitle = `${t('VERSION_TO_COMPARE')} ${leftVersion ?? leftHistoryId}`
	const rightTitle = `${t('VERSION')} ${rightVersion ?? rightHistoryId}`

	const header = () => (
		<DiffHeaderFlex>
			<span>{structureName ?? t('VERSION_DIFF')}</span>
			<Typography sx={{ fontSize: 13, color: '#7A7A7A', fontWeight: 500 }}>
				{leftTitle} → {rightTitle}
			</Typography>
		</DiffHeaderFlex>
	)

	return (
		<Modal
			open
			onClose={onClose}
			contentStyle={MODAL_CONTENT_STYLE}
			bodyStyle={MODAL_BODY_STYLE}
			resizable
			maximizeButtonVisible
			maximized
			headerStyle={MODAL_HEADER_STYLE}
			header={header}
		>
			<StyledDiffWrap data-testid="version-diff-content">
				{skip ? (
					<LoadingPlaceholder data-testid="version-diff-missing-branch">
						{t('VERSIONS_DIFF_NO_BRANCH')}
					</LoadingPlaceholder>
				) : showLoading ? (
					<LoadingPlaceholder data-testid="version-diff-loading">
						{t('LOADING')}
					</LoadingPlaceholder>
				) : isError ? (
					<LoadingPlaceholder data-testid="version-diff-error">
						{t('VERSIONS_DIFF_LOAD_FAILED')}
					</LoadingPlaceholder>
				) : changes.length === 0 ? (
					<LoadingPlaceholder data-testid="version-diff-empty">
						{t('VERSIONS_DIFF_EMPTY')}
					</LoadingPlaceholder>
				) : (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 1,
							fontFamily: 'inherit',
						}}
					>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: '1.4fr 0.8fr 1fr 1fr',
								gap: 1,
								fontSize: 10,
								fontWeight: 700,
								letterSpacing: '.05em',
								textTransform: 'uppercase',
								color: '#9A9A9A',
								px: 0.5,
							}}
						>
							<Box>{t('BRANCHES_DIFF_FIELD')}</Box>
							<Box>{t('BRANCHES_DIFF_CHANGE')}</Box>
							<Box>{t('BRANCHES_DIFF_FROM')}</Box>
							<Box>{t('BRANCHES_DIFF_TO')}</Box>
						</Box>
						{changes.map((change, index) => {
							const style =
								CHANGE_STYLE[change.changeType ?? 'MODIFIED'] ??
								CHANGE_STYLE.MODIFIED
							return (
								<Box
									key={`${change.fieldPath}-${index}`}
									sx={{
										display: 'grid',
										gridTemplateColumns: '1.4fr 0.8fr 1fr 1fr',
										gap: 1,
										alignItems: 'start',
										border: '1px solid #E7E7E7',
										borderRadius: '10px',
										px: 1.5,
										py: 1,
									}}
								>
									<Box
										sx={{
											fontSize: 12,
											fontFamily: 'ui-monospace, monospace',
											color: '#2A2A2A',
											wordBreak: 'break-all',
										}}
									>
										{change.fieldPath ??
											(change.changeType === 'MOVED'
												? t('BRANCHES_DIFF_LOCATION')
												: '—')}
									</Box>
									<Box
										sx={{
											fontSize: 10,
											fontWeight: 700,
											letterSpacing: '.03em',
											color: style.color,
											bgcolor: style.bg,
											borderRadius: '6px',
											px: '7px',
											py: '2.5px',
											width: 'fit-content',
											textTransform: 'uppercase',
										}}
									>
										{change.changeType}
									</Box>
									<Box
										component="pre"
										sx={{
											m: 0,
											fontSize: 11,
											whiteSpace: 'pre-wrap',
											wordBreak: 'break-word',
											color: '#5A5A5A',
										}}
									>
										{formatDiffValue(change.oldValue)}
									</Box>
									<Box
										component="pre"
										sx={{
											m: 0,
											fontSize: 11,
											whiteSpace: 'pre-wrap',
											wordBreak: 'break-word',
											color: '#5A5A5A',
										}}
									>
										{formatDiffValue(change.newValue)}
									</Box>
								</Box>
							)
						})}
					</Box>
				)}
			</StyledDiffWrap>
			<DiffFooterActions>
				<Button
					testId="version-diff-close"
					onClick={onClose}
					schema="transparent"
					icon={faTimes}
				>
					{t('CLOSE')}
				</Button>
			</DiffFooterActions>
		</Modal>
	)
}
